import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

export interface PerWalletResult<T> {
    byWallet: ReadonlyMap<string, T>;
    loadingWallets: ReadonlySet<string>;
    failedWallets: ReadonlySet<string>;
    retryWallet: (wallet: string) => void;
}

export function usePerWallet<T>(
    scope: string | null,
    wallets: readonly string[],
    load: (wallet: string, signal: AbortSignal) => Promise<T>,
    timeoutMs: number
): PerWalletResult<T> {
    const [cache, setCache] = useState<Map<string, T>>(new Map());
    const [loadingWallets, setLoadingWallets] = useState<Set<string>>(
        new Set()
    );
    const [failedWallets, setFailedWallets] = useState<Set<string>>(new Set());
    const inflight = useRef(new Map<string, AbortController>());
    const scopeRef = useRef(scope);
    const loadRef = useRef(load);
    loadRef.current = load;

    useEffect(() => {
        scopeRef.current = scope;
        const controllers = inflight.current;
        for (const controller of controllers.values()) controller.abort();
        controllers.clear();
        setCache(new Map());
        setLoadingWallets(new Set());
        setFailedWallets(new Set());
    }, [scope]);

    const walletsKey = wallets.join(',');
    const walletList = useMemo(
        () => (walletsKey ? walletsKey.split(',') : []),
        [walletsKey]
    );

    useEffect(() => {
        if (scope === null) return;
        const requestScope = scope;
        for (const wallet of walletList) {
            if (
                cache.has(wallet) ||
                failedWallets.has(wallet) ||
                inflight.current.has(wallet)
            ) {
                continue;
            }
            const controller = new AbortController();
            inflight.current.set(wallet, controller);
            setLoadingWallets(prev => new Set(prev).add(wallet));
            let timedOut = false;
            const timer = setTimeout(() => {
                timedOut = true;
                controller.abort();
            }, timeoutMs);
            loadRef
                .current(wallet, controller.signal)
                .then(value => {
                    if (scopeRef.current !== requestScope) return;
                    setCache(prev => new Map(prev).set(wallet, value));
                })
                .catch(err => {
                    if (scopeRef.current !== requestScope) return;
                    if (err?.name === 'AbortError' && !timedOut) return;
                    console.error(`wallet request failed for ${wallet}:`, err);
                    setFailedWallets(prev => new Set(prev).add(wallet));
                })
                .finally(() => {
                    clearTimeout(timer);
                    if (inflight.current.get(wallet) === controller) {
                        inflight.current.delete(wallet);
                    }
                    if (scopeRef.current !== requestScope) return;
                    setLoadingWallets(prev => {
                        const next = new Set(prev);
                        next.delete(wallet);
                        return next;
                    });
                });
        }
    }, [scope, walletList, cache, failedWallets, timeoutMs]);

    useEffect(() => {
        const controllers = inflight.current;
        return () => {
            for (const controller of controllers.values()) controller.abort();
        };
    }, []);

    const retryWallet = useCallback((wallet: string) => {
        setFailedWallets(prev => {
            const next = new Set(prev);
            next.delete(wallet);
            return next;
        });
    }, []);

    const byWallet = useMemo(() => {
        const map = new Map<string, T>();
        for (const wallet of walletList) {
            const value = cache.get(wallet);
            if (value !== undefined) map.set(wallet, value);
        }
        return map;
    }, [cache, walletList]);

    return { byWallet, loadingWallets, failedWallets, retryWallet };
}
